/**
 * ExamApplication Service
 * API integration for exam application (vínculos) management
 *
 * @version 2025.1.0
 * @description Serviço de integração com API de aplicação de exames em 4 níveis organizacionais
 */

import apiClient from './apiClient';
import { ApiResponse } from '../types/common';
import {
  ApplicationLevel,
  ApplicationData,
  ApplyExamRequest,
  UpdateApplicationRequest,
  ExamWithApplication,
  ExamsForTargetResponse,
  ExpirationAlerts,
  ExpiringExam,
  ExpiredExam,
  ExamApplicationSuccessResponse,
} from '../types/examApplication';

// Re-export types for convenience
export * from '../types/examApplication';

// ============================================================================
// RESPONSE TYPE ADAPTERS
// ============================================================================

/**
 * Unwrap API response data
 * httpClient returns ApiResponse<T> which has optional data field
 */
function unwrapData<T>(response: ApiResponse<T>): T {
  if (response.data !== undefined) {
    return response.data;
  }
  // If no data field, return the response itself cast as T
  // This handles cases where the response is the data directly
  return response as unknown as T;
}

// ============================================================================
// SERVICE CLASS
// ============================================================================

class ExamApplicationService {
  private baseUrl = '/exam-applications';

  // ==========================================================================
  // COMPANY LEVEL METHODS
  // ==========================================================================

  /**
   * Get applied and available exams for a company
   */
  async getForCompany(companyId: number): Promise<ExamsForTargetResponse> {
    const response = await apiClient.get<ExamsForTargetResponse>(
      `${this.baseUrl}/company/${companyId}`
    );
    return unwrapData(response);
  }

  /**
   * Apply exam(s) to a company
   */
  async applyToCompany(
    companyId: number,
    request: ApplyExamRequest
  ): Promise<ExamApplicationSuccessResponse> {
    const response = await apiClient.post<ExamApplicationSuccessResponse>(
      `${this.baseUrl}/company/${companyId}/apply`,
      request
    );
    return unwrapData(response);
  }

  /**
   * Update exam application for a company
   */
  async updateForCompany(
    companyId: number,
    examId: number,
    data: UpdateApplicationRequest
  ): Promise<ExamApplicationSuccessResponse> {
    const response = await apiClient.put<ExamApplicationSuccessResponse>(
      `${this.baseUrl}/company/${companyId}/exams/${examId}`,
      data
    );
    return unwrapData(response);
  }

  /**
   * Remove exam application from a company
   */
  async removeFromCompany(
    companyId: number,
    examId: number
  ): Promise<ExamApplicationSuccessResponse> {
    const response = await apiClient.delete<ExamApplicationSuccessResponse>(
      `${this.baseUrl}/company/${companyId}/exams/${examId}`
    );
    return unwrapData(response);
  }

  /**
   * Get expiration alerts for a company
   */
  async getAlertsForCompany(
    companyId: number,
    daysAhead?: number
  ): Promise<ExpirationAlerts> {
    const params = daysAhead !== undefined ? `?daysAhead=${daysAhead}` : '';
    const response = await apiClient.get<ExpirationAlerts>(
      `${this.baseUrl}/company/${companyId}/alerts${params}`
    );
    return unwrapData(response);
  }

  // ==========================================================================
  // DEPARTMENT LEVEL METHODS
  // ==========================================================================

  /**
   * Get applied and available exams for a department
   */
  async getForDepartment(departmentId: number): Promise<ExamsForTargetResponse> {
    const response = await apiClient.get<ExamsForTargetResponse>(
      `${this.baseUrl}/department/${departmentId}`
    );
    return unwrapData(response);
  }

  /**
   * Apply exam(s) to a department
   */
  async applyToDepartment(
    departmentId: number,
    request: ApplyExamRequest
  ): Promise<ExamApplicationSuccessResponse> {
    const response = await apiClient.post<ExamApplicationSuccessResponse>(
      `${this.baseUrl}/department/${departmentId}/apply`,
      request
    );
    return unwrapData(response);
  }

  /**
   * Update exam application for a department
   */
  async updateForDepartment(
    departmentId: number,
    examId: number,
    data: UpdateApplicationRequest
  ): Promise<ExamApplicationSuccessResponse> {
    const response = await apiClient.put<ExamApplicationSuccessResponse>(
      `${this.baseUrl}/department/${departmentId}/exams/${examId}`,
      data
    );
    return unwrapData(response);
  }

  /**
   * Remove exam application from a department
   */
  async removeFromDepartment(
    departmentId: number,
    examId: number
  ): Promise<ExamApplicationSuccessResponse> {
    const response = await apiClient.delete<ExamApplicationSuccessResponse>(
      `${this.baseUrl}/department/${departmentId}/exams/${examId}`
    );
    return unwrapData(response);
  }

  // ==========================================================================
  // OCCUPATION LEVEL METHODS
  // ==========================================================================

  /**
   * Get applied and available exams for an occupation
   */
  async getForOccupation(occupationId: number): Promise<ExamsForTargetResponse> {
    const response = await apiClient.get<ExamsForTargetResponse>(
      `${this.baseUrl}/occupation/${occupationId}`
    );
    return unwrapData(response);
  }

  /**
   * Apply exam(s) to an occupation
   */
  async applyToOccupation(
    occupationId: number,
    request: ApplyExamRequest
  ): Promise<ExamApplicationSuccessResponse> {
    const response = await apiClient.post<ExamApplicationSuccessResponse>(
      `${this.baseUrl}/occupation/${occupationId}/apply`,
      request
    );
    return unwrapData(response);
  }

  /**
   * Update exam application for an occupation
   */
  async updateForOccupation(
    occupationId: number,
    examId: number,
    data: UpdateApplicationRequest
  ): Promise<ExamApplicationSuccessResponse> {
    const response = await apiClient.put<ExamApplicationSuccessResponse>(
      `${this.baseUrl}/occupation/${occupationId}/exams/${examId}`,
      data
    );
    return unwrapData(response);
  }

  /**
   * Remove exam application from an occupation
   */
  async removeFromOccupation(
    occupationId: number,
    examId: number
  ): Promise<ExamApplicationSuccessResponse> {
    const response = await apiClient.delete<ExamApplicationSuccessResponse>(
      `${this.baseUrl}/occupation/${occupationId}/exams/${examId}`
    );
    return unwrapData(response);
  }

  // ==========================================================================
  // EMPLOYEE LEVEL METHODS
  // ==========================================================================

  /**
   * Get applied and available exams for an employee
   */
  async getForEmployee(employeeId: number): Promise<ExamsForTargetResponse> {
    const response = await apiClient.get<ExamsForTargetResponse>(
      `${this.baseUrl}/employee/${employeeId}`
    );
    return unwrapData(response);
  }

  /**
   * Apply exam(s) to an employee
   */
  async applyToEmployee(
    employeeId: number,
    request: ApplyExamRequest
  ): Promise<ExamApplicationSuccessResponse> {
    const response = await apiClient.post<ExamApplicationSuccessResponse>(
      `${this.baseUrl}/employee/${employeeId}/apply`,
      request
    );
    return unwrapData(response);
  }

  /**
   * Update exam application for an employee
   */
  async updateForEmployee(
    employeeId: number,
    examId: number,
    data: UpdateApplicationRequest
  ): Promise<ExamApplicationSuccessResponse> {
    const response = await apiClient.put<ExamApplicationSuccessResponse>(
      `${this.baseUrl}/employee/${employeeId}/exams/${examId}`,
      data
    );
    return unwrapData(response);
  }

  /**
   * Remove exam application from an employee
   */
  async removeFromEmployee(
    employeeId: number,
    examId: number
  ): Promise<ExamApplicationSuccessResponse> {
    const response = await apiClient.delete<ExamApplicationSuccessResponse>(
      `${this.baseUrl}/employee/${employeeId}/exams/${examId}`
    );
    return unwrapData(response);
  }

  // ==========================================================================
  // GENERIC METHODS (by level)
  // ==========================================================================

  /**
   * Get exams for any target level
   */
  async getForTarget(
    level: ApplicationLevel,
    targetId: number
  ): Promise<ExamsForTargetResponse> {
    switch (level) {
      case ApplicationLevel.COMPANY:
        return this.getForCompany(targetId);
      case ApplicationLevel.DEPARTMENT:
        return this.getForDepartment(targetId);
      case ApplicationLevel.OCCUPATION:
        return this.getForOccupation(targetId);
      case ApplicationLevel.EMPLOYEE:
        return this.getForEmployee(targetId);
    }
  }

  /**
   * Apply exam to any target level
   */
  async applyToTarget(
    level: ApplicationLevel,
    targetId: number,
    request: ApplyExamRequest
  ): Promise<ExamApplicationSuccessResponse> {
    switch (level) {
      case ApplicationLevel.COMPANY:
        return this.applyToCompany(targetId, request);
      case ApplicationLevel.DEPARTMENT:
        return this.applyToDepartment(targetId, request);
      case ApplicationLevel.OCCUPATION:
        return this.applyToOccupation(targetId, request);
      case ApplicationLevel.EMPLOYEE:
        return this.applyToEmployee(targetId, request);
    }
  }

  /**
   * Update application for any target level
   */
  async updateForTarget(
    level: ApplicationLevel,
    targetId: number,
    examId: number,
    data: UpdateApplicationRequest
  ): Promise<ExamApplicationSuccessResponse> {
    switch (level) {
      case ApplicationLevel.COMPANY:
        return this.updateForCompany(targetId, examId, data);
      case ApplicationLevel.DEPARTMENT:
        return this.updateForDepartment(targetId, examId, data);
      case ApplicationLevel.OCCUPATION:
        return this.updateForOccupation(targetId, examId, data);
      case ApplicationLevel.EMPLOYEE:
        return this.updateForEmployee(targetId, examId, data);
    }
  }

  /**
   * Remove application from any target level
   */
  async removeFromTarget(
    level: ApplicationLevel,
    targetId: number,
    examId: number
  ): Promise<ExamApplicationSuccessResponse> {
    switch (level) {
      case ApplicationLevel.COMPANY:
        return this.removeFromCompany(targetId, examId);
      case ApplicationLevel.DEPARTMENT:
        return this.removeFromDepartment(targetId, examId);
      case ApplicationLevel.OCCUPATION:
        return this.removeFromOccupation(targetId, examId);
      case ApplicationLevel.EMPLOYEE:
        return this.removeFromEmployee(targetId, examId);
    }
  }

  // ==========================================================================
  // BATCH OPERATIONS
  // ==========================================================================

  /**
   * Apply multiple exams to a target
   */
  async applyMultiple(
    level: ApplicationLevel,
    targetId: number,
    examIds: number[],
    commonData?: ApplicationData
  ): Promise<ExamApplicationSuccessResponse[]> {
    const results: ExamApplicationSuccessResponse[] = [];

    for (const examId of examIds) {
      const request: ApplyExamRequest = {
        examId,
        ...commonData,
      };
      const result = await this.applyToTarget(level, targetId, request);
      results.push(result);
    }

    return results;
  }

  /**
   * Remove multiple exam applications
   */
  async removeMultiple(
    level: ApplicationLevel,
    targetId: number,
    examIds: number[]
  ): Promise<ExamApplicationSuccessResponse[]> {
    const results: ExamApplicationSuccessResponse[] = [];

    for (const examId of examIds) {
      const result = await this.removeFromTarget(level, targetId, examId);
      results.push(result);
    }

    return results;
  }

  // ==========================================================================
  // UTILITY METHODS
  // ==========================================================================

  /**
   * Check if an exam is applied at a target
   */
  isExamApplied(applied: ExamWithApplication[], examId: number): boolean {
    return applied.some((app) => app.examId === examId);
  }

  /**
   * Get applied exams that are expiring or expired
   */
  getExamsWithIssues(applied: ExamWithApplication[]): {
    expiring: ExamWithApplication[];
    expired: ExamWithApplication[];
  } {
    const expiring = applied.filter((app) => app.isExpiringSoon && !app.isExpired);
    const expired = applied.filter((app) => app.isExpired);
    return { expiring, expired };
  }

  /**
   * Count exams by expiration status
   */
  countByStatus(applied: ExamWithApplication[]): {
    ok: number;
    expiring: number;
    expired: number;
  } {
    let ok = 0;
    let expiring = 0;
    let expired = 0;

    for (const app of applied) {
      if (app.isExpired) {
        expired++;
      } else if (app.isExpiringSoon) {
        expiring++;
      } else {
        ok++;
      }
    }

    return { ok, expiring, expired };
  }

  /**
   * Sort exams by expiration (most urgent first)
   */
  sortByUrgency(
    exams: ExamWithApplication[]
  ): ExamWithApplication[] {
    return [...exams].sort((a, b) => {
      // Expired first
      if (a.isExpired && !b.isExpired) return -1;
      if (!a.isExpired && b.isExpired) return 1;

      // Then expiring soon
      if (a.isExpiringSoon && !b.isExpiringSoon) return -1;
      if (!a.isExpiringSoon && b.isExpiringSoon) return 1;

      // Then by days until expiration
      const daysA = a.daysUntilExpiration ?? Infinity;
      const daysB = b.daysUntilExpiration ?? Infinity;
      return daysA - daysB;
    });
  }

  /**
   * Sort alerts by urgency
   */
  sortAlertsByUrgency(
    alerts: (ExpiringExam | ExpiredExam)[]
  ): (ExpiringExam | ExpiredExam)[] {
    return [...alerts].sort((a, b) => {
      // Check if expired (has daysOverdue)
      const aIsExpired = 'daysOverdue' in a;
      const bIsExpired = 'daysOverdue' in b;

      // Expired first
      if (aIsExpired && !bIsExpired) return -1;
      if (!aIsExpired && bIsExpired) return 1;

      // If both expired, sort by days overdue (most overdue first)
      if (aIsExpired && bIsExpired) {
        return (b as ExpiredExam).daysOverdue - (a as ExpiredExam).daysOverdue;
      }

      // If both expiring, sort by days until expiration
      return a.daysUntilExpiration - b.daysUntilExpiration;
    });
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response = await apiClient.get<{ status: string; timestamp: string }>(
      `${this.baseUrl}/health`
    );
    return unwrapData(response);
  }
}

// Export singleton instance
export const examApplicationService = new ExamApplicationService();
export default examApplicationService;
